/* eslint-disable import/no-unresolved */
import React, {
  Suspense, lazy, useEffect, useState,
} from 'react';
import {
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';

import useGA from 'hooks/useGA';
import { Helmet } from 'react-helmet';

import pageTitles from './pageTitles.json';

import bootcampMain from './variants/bootcamp/main.json';
import bootcampEarly from './variants/bootcamp/early.json';
import bootcampUpgrade from './variants/bootcamp/upgrade.json';
import bootcampEvergreen from './variants/bootcamp/evergreen.json';

import ad1 from './variants/bootcamp/ad1.json';
import ad2 from './variants/bootcamp/ad2.json';
import ad3 from './variants/bootcamp/ad3.json';
import ad4 from './variants/bootcamp/ad4.json';
import ad5 from './variants/bootcamp/ad5.json';

import mentorshipApplication from './variants/mentorship/application.json';
import mentorshipApplicationSuccess from './variants/mentorship/success.json';

import mentorshipYTOptin from './variants/mentorship_yt/optin.json';
import mentorshipYTTraining from './variants/mentorship_yt/training.json';
import mentorshipYTForm from './variants/mentorship_yt/form.json';
import mentorshipYTCalendar from './variants/mentorship_yt/calendar.json';
import mentorshipYTSuccess from './variants/mentorship_yt/success.json';

import javaScriptLaunch from './variants/javascript/launch.json';

// Bootcamp V3
import bootcampV3Launch from './variants/bootcamp_v3/launch.json';
import bootcampV3Upgrade from './variants/bootcamp_v3/upgrade.json';
import bootcampV3LaborDay from './variants/bootcamp_v3/labor_day.json';

const Bootcamp = lazy(() => import('views/bootcamp'));
const BootcampStepper = lazy(() => import('views/bootcamp_stepper'));
const JavaScriptRegister = lazy(() => import('views/javascript_register'));
const JavaScriptStepper = lazy(() => import('views/javascript_stepper'));
const JavaScriptSales = lazy(() => import('views/javascript_sales'));
const NFT = lazy(() => import('views/nft'));
const Flashloan = lazy(() => import('views/flashloan'));
const TradingBot = lazy(() => import('views/tradingbot'));
const Airdrop = lazy(() => import('views/airdrop'));
const Agents = lazy(() => import('views/agents'));
const Mentorship = lazy(() => import('views/mentorship'));
const MentorshipYTOptin = lazy(() => import('views/mentorship_yt_optin'));
const MentorshipYTTraining = lazy(() => import('views/mentorship_yt_training'));
const MentorshipYTForm = lazy(() => import('views/mentorship_yt_form'));
const MentorshipYTCalendar = lazy(() => import('views/mentorship_yt_calendar'));
const MentorshipYTSuccess = lazy(() => import('views/mentorship_yt_success'));
const MentorshipYTSuccessDefault = lazy(() => import('views/mentorship_yt_success_default'));
const Sales = lazy(() => import('views/sales'));
const VideoStepper = lazy(() => import('views/videostepper'));
const MentorshipFbOptin = lazy(() => import('views/mentorship_fb_optin'));
const MentorshipFbOptin2 = lazy(() => import('views/mentorship_fb_optin_2'));
const MentorshipFbOptin3 = lazy(() => import('views/mentorship_fb_optin_3'));

const AgenticCoding = lazy(() => import('views/agentic_coding'));

// Bootcamp V3
const BootcampV3 = lazy(() => import('views/bootcamp_v3'));
const BootcampV3Register = lazy(() => import('views/bootcamp_v3_register'));
const BootcampV3Stepper = lazy(() => import('views/bootcamp_v3_stepper'));

function App() {
  useGA();
  const location = useLocation();
  const [pageTitle, setPageTitle] = useState('');

  useEffect(() => {
    if (location.pathname !== pageTitle) {
      setPageTitle(pageTitles[location.pathname]);
    }
  }, [location.pathname]);
  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <Suspense fallback={<div />}>
        <Routes>
          {/* Bootcamp Main Launch */}
          <Route path="/bootcamp-f7edb117" element={<Bootcamp variant={bootcampMain} />} />

          {/* Bootcamp V3 Opt-in */}
          <Route path="/bootcamp" element={<BootcampV3Register success={false} />} />
          <Route path="/bootcamp-success" element={<BootcampV3Register success />} />

          {/* Bootcamp V3 Launch */}
          <Route path="/bootcamp-kt6q02vx" element={<BootcampV3 variant={bootcampV3Launch} />} />

          {/* Bootcamp V3 Upgrade */}
          <Route path="/bootcamp-tby2rq0k" element={<BootcampV3 variant={bootcampV3Upgrade} />} />

          {/* Bootcamp V3 Labor Day Sale */}
          <Route path="/bootcamp-swsbalmf" element={<BootcampV3 variant={bootcampV3LaborDay} />} />

          {/* Bootcamp V3 Video Stepper */}
          <Route path="/bootcamp-g7x2m9lq" element={<BootcampV3Stepper />} />
          <Route path="/bootcamp-b4t6z1yd" element={<BootcampV3Stepper />} />
          <Route path="/bootcamp-p8n3w5vk" element={<BootcampV3Stepper />} />

          {/* Bootcamp Main Launch & Bootcamp Upgrade - Video Stepper */}
          <Route path="/bootcamp-2462bf4f" element={<BootcampStepper variant={bootcampMain.training_videos} />} />
          <Route path="/bootcamp-31d47c4c" element={<BootcampStepper variant={bootcampMain.training_videos} />} />
          <Route path="/bootcamp-8e8d0124" element={<BootcampStepper variant={bootcampMain.training_videos} />} />

          {/* Bootcamp Early Access - $499 / $749 / $999 */}
          <Route path="/bootcamp-a08dcdd2" element={<Bootcamp variant={bootcampEarly.low} />} />
          <Route path="/bootcamp-49ba8569" element={<Bootcamp variant={bootcampEarly.medium} />} />
          <Route path="/bootcamp-dfcc5a99" element={<Bootcamp variant={bootcampEarly.high} />} />

          {/* Bootcamp Early Access - Video Stepper */}
          <Route path="/bootcamp-678d10ef" element={<BootcampStepper variant={bootcampEarly.training_videos} />} />
          <Route path="/bootcamp-1282a1fe" element={<BootcampStepper variant={bootcampEarly.training_videos} />} />
          <Route path="/bootcamp-073dd36a" element={<BootcampStepper variant={bootcampEarly.training_videos} />} />

          {/* Bootcamp Upgrade */}
          <Route path="/bootcamp-rby63a21" element={<Bootcamp variant={bootcampUpgrade} />} />

          {/* Bootcamp Evergreen */}
          <Route path="/bootcamp-47d807bb" element={<Bootcamp variant={bootcampEvergreen} />} />

          {/* Bootcamp Evergreen - Video Stepper */}
          <Route path="/bootcamp-9245149b" element={<BootcampStepper variant={bootcampEvergreen.training_videos} />} />
          <Route path="/bootcamp-2c61ccca" element={<BootcampStepper variant={bootcampEvergreen.training_videos} />} />
          <Route path="/bootcamp-8e4c800d" element={<BootcampStepper variant={bootcampEvergreen.training_videos} />} />

          {/* Retargeting Ads */}
          <Route path="/bootcamp-58e918cc" element={<Bootcamp variant={ad1} />} />
          <Route path="/bootcamp-69f020dd" element={<Bootcamp variant={ad2} />} />
          <Route path="/bootcamp-70g131ee" element={<Bootcamp variant={ad3} />} />
          <Route path="/bootcamp-81h242ff" element={<Bootcamp variant={ad4} />} />
          <Route path="/bootcamp-92i353gg" element={<Bootcamp variant={ad5} />} />

          {/* JavaScript Course Opt-in */}
          <Route path="/javascript" element={<JavaScriptRegister success={false} />} />
          <Route path="/javascript-success" element={<JavaScriptRegister success />} />

          {/* JavaScript Video Stepper */}
          <Route path="/javascript-fr6hna18" element={<JavaScriptStepper />} />
          <Route path="/javascript-lk8q1vm0" element={<JavaScriptStepper />} />
          <Route path="/javascript-ty8c2n10" element={<JavaScriptStepper />} />

          {/* JavaScript Sales Page */}
          <Route path="/javascript-gb8q2p2f" element={<JavaScriptSales variant={javaScriptLaunch} />} />

          {/* NFT Masterclass */}
          <Route path="/nft-masterclass-k1bc12k40" element={<NFT />} />

          {/* Flashloan Masterclass */}
          <Route path="/flash-k1ab3q9" element={<Flashloan />} />

          {/* Trading Bot Masterclass */}
          <Route path="/trading-f24ac45" element={<TradingBot />} />

          {/* Airdrop Masterclass */}
          <Route path="/airdrop-g97xj2f" element={<Airdrop />} />

          {/* AI Agents Masterclass */}
          <Route path="/ai-agents-03xt8qk" element={<Agents />} />

          {/* Mentorship */}
          <Route path="/mentorship-application-18vkj3k" element={<Mentorship variant={mentorshipApplication.first} />} />
          <Route path="/mentorship-application-13lrpq9" element={<Mentorship variant={mentorshipApplication.second} />} />
          <Route path="/mentorship-application-4rg89za" element={<Mentorship variant={mentorshipApplication.third} />} />
          <Route path="/mentorship-application-90pwr50" element={<Mentorship variant={mentorshipApplication.forth} />} />
          <Route path="/mentorship-application-p1v43ou" element={<Mentorship variant={mentorshipApplication.fifth} />} />
          <Route path="/mentorship-application-18f6qb2" element={<Mentorship variant={mentorshipApplication.sixth} />} />

          {/* Mentorship Application Success Video */}
          <Route path="/mentorship-application-success-10vokd" element={<Mentorship variant={mentorshipApplicationSuccess.video} />} />

          {/* Mentorship Application Success No Video */}
          <Route path="/mentorship-application-success-67hn2d" element={<Mentorship variant={mentorshipApplicationSuccess.default} />} />

          {/* Mentorship Youtube Ads Optins */}
          <Route path="yt-l99e8id" element={<MentorshipYTOptin variant={mentorshipYTOptin.first} />} />
          <Route path="yt-7lwc12p" element={<MentorshipYTOptin variant={mentorshipYTOptin.second} />} />
          <Route path="yt-to2g5dy" element={<MentorshipYTOptin variant={mentorshipYTOptin.third} />} />
          <Route path="yt-gw2uj6t" element={<MentorshipYTOptin variant={mentorshipYTOptin.forth} />} />
          <Route path="yt-18f6qb2" element={<MentorshipYTOptin variant={mentorshipYTOptin.fifth} />} />

          {/* Mentorship Youtube Ads Trainings */}
          <Route path="yt-training-l99e8id" element={<MentorshipYTTraining variant={mentorshipYTTraining.first} />} />
          <Route path="yt-training-7lwc12p" element={<MentorshipYTTraining variant={mentorshipYTTraining.second} />} />
          <Route path="yt-training-to2g5dy" element={<MentorshipYTTraining variant={mentorshipYTTraining.third} />} />
          <Route path="yt-training-gw2uj6t" element={<MentorshipYTTraining variant={mentorshipYTTraining.forth} />} />
          <Route path="yt-training-18f6qb2" element={<MentorshipYTTraining variant={mentorshipYTTraining.fifth} />} />

          {/* Mentorship Youtube Ads Form */}
          <Route path="yt-form-l99e8id" element={<MentorshipYTForm variant={mentorshipYTForm.first} />} />
          <Route path="yt-form-7lwc12p" element={<MentorshipYTForm variant={mentorshipYTForm.second} />} />
          <Route path="yt-form-to2g5dy" element={<MentorshipYTForm variant={mentorshipYTForm.third} />} />
          <Route path="yt-form-gw2uj6t" element={<MentorshipYTForm variant={mentorshipYTForm.forth} />} />
          <Route path="yt-form-18f6qb2" element={<MentorshipYTForm variant={mentorshipYTForm.fifth} />} />

          {/* Mentorship Youtube Ads Calendar */}
          <Route path="yt-calendar-l99e8id" element={<MentorshipYTCalendar variant={mentorshipYTCalendar.first} />} />
          <Route path="yt-calendar-7lwc12p" element={<MentorshipYTCalendar variant={mentorshipYTCalendar.second} />} />
          <Route path="yt-calendar-to2g5dy" element={<MentorshipYTCalendar variant={mentorshipYTCalendar.third} />} />
          <Route path="yt-calendar-gw2uj6t" element={<MentorshipYTCalendar variant={mentorshipYTCalendar.forth} />} />
          <Route path="yt-calendar-18f6qb2" element={<MentorshipYTCalendar variant={mentorshipYTCalendar.fifth} />} />

          {/* Mentorship Youtube Ads Success */}
          <Route path="yt-success-l99e8id" element={<MentorshipYTSuccess variant={mentorshipYTSuccess.first} />} />
          <Route path="yt-success-7lwc12p" element={<MentorshipYTSuccess variant={mentorshipYTSuccess.second} />} />
          <Route path="yt-success-to2g5dy" element={<MentorshipYTSuccess variant={mentorshipYTSuccess.third} />} />
          <Route path="yt-success-gw2uj6t" element={<MentorshipYTSuccess variant={mentorshipYTSuccess.forth} />} />
          <Route path="yt-success-18f6qb2" element={<MentorshipYTSuccess variant={mentorshipYTSuccess.fifth} />} />

          {/* Mentorship Youtube Ads Success Default */}
          <Route path="yt-success-ht4alvk" element={<MentorshipYTSuccessDefault />} />

          {/* Mentorship Application Success No Video */}
          <Route path="yt-application-success-67hn2d" element={<MentorshipYTOptin variant={mentorshipYTSuccess.default} />} />

          {/* Mentorship Facebook  Ads Optins */}
          <Route path="fb-l99e8id" element={<MentorshipFbOptin />} />
          <Route path="fb-l99e8id2" element={<MentorshipFbOptin2 />} />
          <Route path="fb-l99e8id3" element={<MentorshipFbOptin3 />} />

          {/* BlockSight */}
          <Route path="/bs-launch-k2ab92x" element={<Sales />} />
          <Route path="/bs-day-1ph4kd5" element={<VideoStepper />} />
          <Route path="/bs-day-209fk21j" element={<VideoStepper />} />
          <Route path="/bs-day-1kzh46ia" element={<VideoStepper />} />
          <Route path="/bs-day-1kzh46ib" element={<VideoStepper />} />

          {/* Agentic Coding */}
          <Route path="/agentic-coding-2v892Ok" element={<AgenticCoding />} />

          {/* DEFAULT LINK TO SHOW ON ANY OTHER ROUTES */}
          <Route path="*" element={<Navigate to="/trading-f24ac45" />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;
